import React, { useRef } from 'react';
import styled from 'styled-components';
import { TextBlockData, ProjectElementDataWithText } from '../../shared/dto/EntityRepository';
import { TextBlockType, ProjectElementType } from '../../shared/enum/EnumRepository';
import { useImportStationTextStore } from '../../shared/store/importStationTextStore';
import { useProjectElementState } from '../../shared/hooks/useApi/useProjectElement';
import { useOptimizedImageUrl } from '../../shared/hooks/useOptimizedImageUrl';
import defaultImg from '../../asset/project/default_1.png';

interface SimpleTextBlockProps {
  data: TextBlockData;
  onClickHandler?: (textBlockId: string) => void;
  projectElementId?: string;
  /** true면 새 TEXT 엘리먼트용 다중 선택(클릭 시 토글), Import 버튼으로 한 번에 가져오기 */
  selectModeForNewElement?: boolean;
}

const SimpleTextBlock: React.FC<SimpleTextBlockProps> = ({
  data,
  onClickHandler,
  projectElementId,
  selectModeForNewElement,
}) => {
  const { selectedSubIdList, addSelectedSubId, removeSelectedSubId } = useImportStationTextStore();
  const { projectElementList } = useProjectElementState();

  const thumbContainerRef = useRef<HTMLDivElement>(null);
  const imageUrl = useOptimizedImageUrl(data.type === TextBlockType.IMAGE ? data.uploadFile?.originUrl ?? '' : '', thumbContainerRef);

  const isAlreadyInProject = (() => {
    if (projectElementId) {
      const pe = projectElementList.find((p) => p.id === projectElementId);
      if (!pe || pe.projectElementType !== ProjectElementType.TEXT) return false;
      const textPe = pe as ProjectElementDataWithText;
      return textPe.selectedTextBlocks.some((tb) => tb.id === data.id);
    }
    // projectElementId 없음(ImportStationTextSub): 프로젝트 내 모든 TEXT 엘리먼트에서 사용 중이면 선택 불가
    return projectElementList.some((p) => {
      if (p.projectElementType !== ProjectElementType.TEXT) return false;
      const textPe = p as ProjectElementDataWithText;
      return textPe.selectedTextBlocks.some((tb) => tb.id === data.id);
    });
  })();

  const isSelectMode = projectElementId != null || selectModeForNewElement === true;

  const renderTextBlock = () => {
    switch (data.type) {
      case TextBlockType.HEADING:
        return (
          <Heading>{data.textContent || ""}</Heading>
        );
      case TextBlockType.PARAGRAPH:
        return (
          <Description>{data.textContent || ""}</Description>
        );
      case TextBlockType.QUOTE:
        return (
          <Quote>{data.textContent || ""}</Quote>
        );
      case TextBlockType.SOURCE_LINE:
        return (
          <SourceLine>
            {data.textContent || ""}
          </SourceLine>
        );
      case TextBlockType.LINK:
        return (
          <Description>
            {data.textContent || data.url || ""}
          </Description>
        );
      case TextBlockType.IMAGE:
        return (
          <>
          <Image ref={thumbContainerRef}>
            <ThumbImg src={imageUrl === '' ? defaultImg : imageUrl} alt="" />
          </Image>
          <SourceLine>
            {data.caption || ""}
          </SourceLine>
          </>
        );
      default:
        return null;
    }
  }

  const isSelected = selectedSubIdList.includes(data.id);
  const isSelectedOrInProject = isAlreadyInProject || (isSelectMode && isSelected);

  const handleClick = () => {
    if (isAlreadyInProject) return;
    if (onClickHandler && !isSelectMode) {
      onClickHandler(data.id);
      return;
    }
    if (isSelectMode) {
      if (isSelected) {
        removeSelectedSubId(data.id);
      } else {
        addSelectedSubId(data.id);
      }
    }
  };

  return (
    <SimpleTextBlockComp
      $isSelectedOrInProject={isSelectedOrInProject}
      onClick={handleClick}
    >
      {renderTextBlock()}
    </SimpleTextBlockComp>
  );
}

export default SimpleTextBlock;

const SimpleTextBlockComp = styled.div<{ $isSelectedOrInProject?: boolean }>`
  position: relative;
  width: 100%;
  margin-bottom: 12px;
  padding: 10px;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  box-shadow: ${({ $isSelectedOrInProject }) =>
    $isSelectedOrInProject
      ? 'inset 1px 1px 3px 0 rgba(0, 0, 0, 0.25), inset -1px -1px 5px 0 rgba(255, 255, 255, 0.7)'
      : 'inset 1px 1px 3px 0 rgba(255, 255, 255, 0.3), inset -1px -1px 3px 0 rgba(0, 0, 0, 0.1)'};
  opacity: ${({ $isSelectedOrInProject }) => $isSelectedOrInProject ? 0.5 : 1};
  cursor: ${({ $isSelectedOrInProject }) => ($isSelectedOrInProject ? 'not-allowed' : 'pointer')};
  &:hover {
    transform: scale(0.99);
  }
  transition: all 0.2s ease-in-out;
`;

const Heading = styled.div`
  width: 100%;
  ${({ theme }) => theme.typography.Body_03_1};
`
const Description = styled.div`
  width: 100%;
  line-height: 1.7;
  ${({ theme }) => theme.typography.Body_03_2};
`

const Quote = styled.div`
  width: 100%;
  text-align: center;
  line-height: 1.6;
  font-style: italic;
  ${({ theme }) => theme.typography.Body_03_2};
`

const SourceLine = styled.div`
  width: 100%;
  text-align: right;
  line-height: 1.4;
  ${({ theme }) => theme.typography.Body_04};
`

const Image = styled.div`
  display: flex;
  align-items: center;
`

const ThumbImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;